import React, { useState, useEffect } from 'react';
import {
    Stethoscope, Calendar, Users, Settings,
    ArrowRight, CheckCircle2,
    LayoutDashboard, MessageSquare, UserPlus,
    Zap, BookOpen, Play, ChevronRight,
    Mail, Star, ChevronDown, Home,
    Send, ClipboardList, Maximize2, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ─── Section data ─────────────────────────────────────────────────────────────
type EmbedTab = 'overview' | 'calendar' | 'patients' | 'records' | 'settings' | 'communications' | 'pos';

const SECTIONS: {
    id: string;
    icon: any;
    color: string;
    title: string;
    subtitle: string;
    tab: EmbedTab;
    steps: { title: string; desc: string; tip: string }[];
}[] = [
    {
        id: 'inicio', icon: LayoutDashboard, color: 'indigo', tab: 'overview',
        title: 'Panel Principal', subtitle: 'Vista general de tu clínica',
        steps: [
            { title: 'Accede al dashboard', desc: 'Ingresa en amigopeludo.cl/veterinarias con tu correo y contraseña. Si es tu primer ingreso usa la clave temporal que te enviamos al correo.', tip: 'Puedes cambiar tu contraseña en Ajustes → Perfil en cualquier momento.' },
            { title: 'Resumen del día', desc: 'El panel principal muestra las citas del día, pacientes recientes, alertas pendientes y estadísticas de la clínica en tiempo real.', tip: 'Los números grandes en la parte superior muestran citas hoy, pacientes totales y comunicaciones pendientes.' },
            { title: 'Navegación lateral', desc: 'El menú izquierdo da acceso rápido a: Panel, Agenda, Comunicaciones, Pacientes y Ajustes. En móvil se despliega con el ícono de menú.', tip: 'Puedes personalizar los colores del sidebar en Ajustes → Entorno Visual.' },
        ],
    },
    {
        id: 'agenda', icon: Calendar, color: 'violet', tab: 'calendar',
        title: 'Agenda Médica', subtitle: 'Gestiona tus citas y horarios',
        steps: [
            { title: 'Crear una nueva cita', desc: 'Haz clic en "Nueva Cita" (botón superior derecho del calendario). Busca la mascota por nombre, elige fecha, hora y motivo de consulta.', tip: 'Si la mascota no está registrada, puedes agregarla directamente desde el buscador con "Agregar Nuevo Paciente".' },
            { title: 'Vistas disponibles', desc: 'Alterna entre vista Día, Semana y Mes. Cada vista tiene su función según el nivel de detalle que necesites.', tip: 'La vista Semana es ideal para ver disponibilidad de la semana de un vistazo rápido.' },
            { title: 'Agendamiento por veterinario', desc: 'Si tienes equipo registrado, al crear una cita podrás asignarla a un médico específico. Los filtros de color en la agenda permiten ver solo las citas de cada vet.', tip: 'Los puntos de color en la barra izquierda de cada cita indican qué médico está asignado.' },
        ],
    },
    {
        id: 'pacientes', icon: Users, color: 'emerald', tab: 'patients',
        title: 'Mascotas Vinculadas', subtitle: 'Tus pacientes siempre a mano',
        steps: [
            { title: 'Ver todos los pacientes', desc: 'La sección "Mascotas Vinculadas" muestra todas las mascotas que han sido atendidas o agendadas en tu clínica.', tip: 'Las mascotas se vinculan automáticamente cuando agendas una cita o cuando un dueño reserva contigo desde la app.' },
            { title: 'Buscar un paciente', desc: 'Usa el buscador superior para filtrar por nombre de mascota o nombre del propietario. La búsqueda es instantánea.', tip: 'Busca por el apellido del dueño si no recuerdas el nombre exacto de la mascota.' },
            { title: 'Abrir la ficha médica', desc: 'Haz clic en cualquier tarjeta de mascota para abrir su ficha completa: datos, historial clínico, vacunas y alertas.', tip: 'Las alergias y condiciones crónicas aparecen destacadas en rojo para que las veas antes de atender.' },
        ],
    },
    {
        id: 'ficha', icon: ClipboardList, color: 'sky', tab: 'records',
        title: 'Ficha del Paciente', subtitle: 'Historial clínico completo',
        steps: [
            { title: 'Datos del paciente', desc: 'La barra lateral de la ficha muestra: foto, nombre, raza, edad calculada automáticamente, sexo, peso, número de microchip y alertas de alergias.', tip: 'El peso se actualiza con cada nueva consulta que registres en el workspace.' },
            { title: 'Registrar una vacuna', desc: 'Haz clic en "Añadir Vacuna". Selecciona el tipo de vacuna de la lista predefinida y la fecha de aplicación. Puedes agregar la fecha de próxima dosis.', tip: 'Las vacunas aparecen en el Carnet Sanitario del dueño dentro de la app Amigo Peludo.' },
            { title: 'Emitir una receta', desc: 'Usa "Emitir Receta" para generar una receta médica digital con medicamentos, dosis, frecuencia y duración del tratamiento.', tip: 'Puedes agregar múltiples medicamentos con dosis y frecuencia en una sola receta.' },
            { title: 'Registrar exámenes', desc: 'El botón "Exámenes" permite adjuntar resultados de laboratorio, imágenes de radiografías u otros documentos médicos al historial.', tip: 'Sube imágenes en formato JPG, PNG o PDF directamente desde tu computador o celular.' },
        ],
    },
    {
        id: 'comunicaciones', icon: MessageSquare, color: 'amber', tab: 'communications',
        title: 'Comunicaciones', subtitle: 'Mantén informados a los dueños',
        steps: [
            { title: 'Alertas pendientes', desc: 'La sección Comunicaciones muestra mascotas con vacunas vencidas, controles próximos y desparasitaciones pendientes detectadas automáticamente.', tip: 'Las alertas se generan en base a las fechas de las últimas vacunas y controles registrados.' },
            { title: 'Enviar notificaciones', desc: 'Selecciona una o varias alertas y haz clic en "Enviar". El dueño recibe un aviso en su app Amigo Peludo recordándole la cita o control pendiente.', tip: 'Usa "Sel. Todo" para enviar recordatorios masivos a todos los dueños con alertas activas.' },
        ],
    },
    {
        id: 'pos', icon: Send, color: 'rose', tab: 'pos',
        title: 'Punto de Venta', subtitle: 'Cobros y ventas integradas',
        steps: [
            { title: 'Crear un cobro', desc: 'Desde el módulo POS puedes generar cobros de servicios (consultas, procedimientos) o venta de productos. Todo queda registrado con fecha y método de pago.', tip: 'Si tienes terminal Tuu, MercadoPago Point o Transbank configurado, el cobro se procesa directamente desde aquí.' },
            { title: 'Métodos de pago', desc: 'Acepta efectivo, transferencia, débito y crédito. Configura tus terminales en Ajustes → Terminales antes de usar este módulo.', tip: 'Los cobros quedan asociados a la ficha del paciente para llevar un historial de gastos por mascota.' },
        ],
    },
    {
        id: 'ajustes', icon: Settings, color: 'slate', tab: 'settings',
        title: 'Configuración', subtitle: 'Personaliza tu entorno',
        steps: [
            { title: 'Perfil de la clínica', desc: 'En Ajustes → Perfil puedes actualizar el nombre de la clínica, dirección, teléfono, logotipo y datos del director médico.', tip: 'Mantén el correo actualizado ya que es el canal principal de comunicaciones del sistema.' },
            { title: 'Entorno visual', desc: 'Personaliza los colores del dashboard en tiempo real: color principal, fondo del sidebar, y otros elementos del diseño.', tip: 'Los cambios de color se aplican en tiempo real para que veas el resultado antes de guardar.' },
            { title: 'Terminales de pago', desc: 'Conecta tu máquina de pago en Ajustes → Terminales. Compatible con Tuu, MercadoPago Point y Transbank Smart POS.', tip: 'Una vez configurado, podrás cobrar débito y crédito directamente desde el módulo POS del dashboard.' },
            { title: 'Equipo médico', desc: 'En Ajustes → Equipo puedes registrar a cada veterinario de tu clínica con su nombre, especialidad, RUT, teléfono, correo y color de agenda.', tip: 'El color que elijas para cada médico aparece en la agenda y en los filtros por veterinario.' },
        ],
    },
    {
        id: 'registro', icon: UserPlus, color: 'teal', tab: 'patients',
        title: 'Registro Rápido', subtitle: 'Agrega pacientes al instante',
        steps: [
            { title: 'Registrar dueño y mascota', desc: 'Al agendar una cita, si la mascota no existe haz clic en "Agregar Nuevo Paciente". Ingresa los datos del dueño y la mascota sin salir del modal.', tip: 'El sistema crea la cuenta del dueño y le envía un correo con sus credenciales de acceso a la app.' },
            { title: 'Datos mínimos requeridos', desc: 'Solo necesitas: nombre del dueño, correo electrónico, nombre de la mascota y especie. El resto puede completarse después desde la ficha.', tip: 'La mascota queda vinculada automáticamente a tu clínica al momento del registro.' },
        ],
    },
];

const COLOR_MAP: Record<string, { bg: string; text: string; light: string; border: string; badge: string }> = {
    indigo:  { bg: 'bg-indigo-600',  text: 'text-indigo-600',  light: 'bg-indigo-50',  border: 'border-indigo-200',  badge: 'bg-indigo-100 text-indigo-700'  },
    violet:  { bg: 'bg-violet-600',  text: 'text-violet-600',  light: 'bg-violet-50',  border: 'border-violet-200',  badge: 'bg-violet-100 text-violet-700'  },
    emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
    sky:     { bg: 'bg-sky-600',     text: 'text-sky-600',     light: 'bg-sky-50',     border: 'border-sky-200',     badge: 'bg-sky-100 text-sky-700'     },
    amber:   { bg: 'bg-amber-500',   text: 'text-amber-600',   light: 'bg-amber-50',   border: 'border-amber-200',   badge: 'bg-amber-100 text-amber-700'   },
    rose:    { bg: 'bg-rose-600',    text: 'text-rose-600',    light: 'bg-rose-50',    border: 'border-rose-200',    badge: 'bg-rose-100 text-rose-700'    },
    slate:   { bg: 'bg-slate-700',   text: 'text-slate-700',   light: 'bg-slate-50',   border: 'border-slate-200',   badge: 'bg-slate-100 text-slate-700'   },
    teal:    { bg: 'bg-teal-600',    text: 'text-teal-600',    light: 'bg-teal-50',    border: 'border-teal-200',    badge: 'bg-teal-100 text-teal-700'    },
};

// ─── Iframe embed ─────────────────────────────────────────────────────────────
const DashboardEmbed: React.FC<{ tab: EmbedTab; height?: number }> = ({ tab, height = 680 }) => {
    const [loaded, setLoaded] = useState(false);

    return (
        <div style={{ position: 'relative', width: '100%', height, borderRadius: '20px', overflow: 'hidden', border: '1.5px solid #e2e8f0', boxShadow: '0 8px 32px rgba(0,0,0,.10)' }}>
            {!loaded && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', zIndex: 10 }}>
                    <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
                    <span style={{ fontSize: '12px', fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.1em' }}>Cargando vista real…</span>
                </div>
            )}
            <iframe
                src={`/veterinarias/embed?tab=${tab}`}
                title={`Dashboard — ${tab}`}
                onLoad={() => setLoaded(true)}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block', opacity: loaded ? 1 : 0, transition: 'opacity .3s' }}
                allow="same-origin"
            />
        </div>
    );
};

// ─── Fullscreen modal ─────────────────────────────────────────────────────────
const FullscreenModal: React.FC<{ tab: EmbedTab; title: string; onClose: () => void }> = ({ tab, title, onClose }) => {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,.88)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', padding: '16px', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80' }} />
                    <span style={{ fontSize: '11px', fontWeight: 900, color: 'white', textTransform: 'uppercase', letterSpacing: '.15em' }}>{title} — Pantalla Completa</span>
                </div>
                <button onClick={onClose} style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', color: 'white', borderRadius: '10px', padding: '6px 14px', fontSize: '11px', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    <X size={13} /> Cerrar (ESC)
                </button>
            </div>
            <div style={{ flex: 1, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,.1)' }}>
                <iframe
                    src={`/veterinarias/embed?tab=${tab}`}
                    title={`Dashboard fullscreen — ${tab}`}
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                    allow="same-origin"
                />
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const VetTutorial: React.FC = () => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [activeStep, setActiveStep] = useState<Record<string, number>>({});
    const [fullscreen, setFullscreen] = useState<{ tab: EmbedTab; title: string } | null>(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const toggleSection = (id: string) =>
        setActiveSection(prev => (prev === id ? null : id));

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

            {fullscreen && (
                <FullscreenModal
                    tab={fullscreen.tab}
                    title={fullscreen.title}
                    onClose={() => setFullscreen(null)}
                />
            )}

            {/* Navbar */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100' : 'bg-white border-b border-slate-100'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/veterinarias')} className="flex items-center gap-2 group">
                            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                                <Stethoscope size={16} />
                            </div>
                            <span className="font-black text-slate-800 italic uppercase tracking-tighter text-sm hidden sm:inline">Amigo Peludo Pro</span>
                        </button>
                        <ChevronRight size={14} className="text-slate-300 hidden sm:inline" />
                        <span className="font-black text-indigo-600 uppercase text-[10px] tracking-widest hidden sm:inline">Tutoriales</span>
                    </div>
                    <button onClick={() => navigate('/veterinarias')} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-600 transition-colors px-3 py-2 rounded-xl hover:bg-indigo-50">
                        <Home size={14} /> Volver al Portal
                    </button>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-24 pb-16 px-4 sm:px-6 bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/5 rounded-full" />
                    <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-white/5 rounded-full" />
                </div>
                <div className="max-w-5xl mx-auto relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-white/20">
                        <BookOpen size={12} /> Guía interactiva del sistema
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase italic tracking-tighter text-white leading-none mb-4">
                        Centro de<br />Tutoriales
                    </h1>
                    <p className="text-indigo-200 text-base sm:text-lg font-medium max-w-xl mx-auto mb-4">
                        Cada sección muestra el panel veterinario real — interactúa directamente con la interfaz sin salir del tutorial.
                    </p>
                    <p className="text-indigo-300 text-sm font-medium max-w-lg mx-auto mb-8">
                        Aprende a gestionar citas veterinarias online, historial clínico digital, control de inventario de medicamentos, marketing para veterinarias y posicionamiento en el mapa de servicios veterinarios de tu ciudad.
                    </p>
                    <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
                        {[
                            { n: SECTIONS.length, label: 'Módulos' },
                            { n: SECTIONS.reduce((a, s) => a + s.steps.length, 0), label: 'Pasos' },
                            { n: '~15', label: 'Minutos' },
                        ].map(s => (
                            <div key={s.label} className="bg-white/10 rounded-2xl p-3 border border-white/10">
                                <p className="text-2xl font-black text-white">{s.n}</p>
                                <p className="text-[9px] font-black text-indigo-300 uppercase tracking-widest">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Sections */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-5">
                {SECTIONS.map((section, sIdx) => {
                    const c = COLOR_MAP[section.color];
                    const SIcon = section.icon;
                    const isOpen = activeSection === section.id;
                    const currentStep = activeStep[section.id] ?? 0;

                    return (
                        <div
                            key={section.id}
                            id={section.id}
                            className={`bg-white rounded-[2.5rem] border shadow-sm overflow-hidden transition-all duration-300 ${isOpen ? `border-2 ${c.border} shadow-xl` : 'border-slate-100 hover:border-slate-200 hover:shadow-md'}`}
                        >
                            {/* Header */}
                            <button onClick={() => toggleSection(section.id)} className="w-full flex items-center gap-5 p-7 text-left">
                                <div className={`w-14 h-14 ${c.bg} rounded-2xl flex items-center justify-center text-white shrink-0 shadow-md`}>
                                    <SIcon size={24} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${c.badge} px-2 py-0.5 rounded-full`}>Módulo {sIdx + 1}</span>
                                        <span className="text-[9px] font-bold text-slate-400 uppercase">{section.steps.length} pasos</span>
                                    </div>
                                    <h2 className="text-2xl font-black text-slate-900 uppercase italic tracking-tighter leading-tight">{section.title}</h2>
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{section.subtitle}</p>
                                </div>
                                <div className={`shrink-0 p-2 rounded-xl transition-all ${isOpen ? `${c.light} ${c.text}` : 'text-slate-300'}`}>
                                    <ChevronDown size={22} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                                </div>
                            </button>

                            {/* Body */}
                            <div className={`transition-all duration-500 overflow-hidden ${isOpen ? 'max-h-[2400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className={`mx-6 mb-7 rounded-[1.5rem] overflow-hidden border ${c.border}`}>

                                    {/* Step tabs */}
                                    <div className={`flex ${c.light} border-b ${c.border} overflow-x-auto`} style={{ scrollbarWidth: 'none' }}>
                                        {section.steps.map((step, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setActiveStep(p => ({ ...p, [section.id]: i }))}
                                                className={`flex items-center gap-2 px-5 py-3.5 text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap shrink-0 border-b-2 ${currentStep === i ? `${c.text} border-current bg-white` : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                                            >
                                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black shrink-0 ${currentStep === i ? `${c.bg} text-white` : 'bg-slate-200 text-slate-500'}`}>{i + 1}</span>
                                                {step.title}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Per-step content */}
                                    {section.steps.map((step, i) => (
                                        <div key={i} className={currentStep === i ? 'block' : 'hidden'}>

                                            {/* Instruction bar */}
                                            <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 px-7 py-5 bg-white border-b ${c.border}`}>
                                                <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center text-white font-black shrink-0 shadow text-base`}>{i + 1}</div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-black text-slate-900 uppercase italic tracking-tighter text-lg leading-tight">{step.title}</h3>
                                                    <p className="text-slate-600 text-sm font-medium leading-relaxed mt-0.5">{step.desc}</p>
                                                </div>
                                                <div className={`shrink-0 flex items-start gap-2 ${c.light} rounded-xl px-4 py-3 border ${c.border} max-w-xs w-full sm:w-auto`}>
                                                    <Zap size={13} className={`${c.text} shrink-0 mt-0.5`} />
                                                    <p className={`text-xs font-bold ${c.text} leading-relaxed`}>
                                                        <span className="font-black uppercase tracking-widest text-[9px] block mb-0.5">Consejo</span>
                                                        {step.tip}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Dashboard embed */}
                                            <div className={`p-5 ${c.light}`}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${c.bg} animate-pulse`} />
                                                        <span className={`text-[9px] font-black uppercase tracking-widest ${c.text}`}>
                                                            Vista real — interactúa libremente con el sistema
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={() => setFullscreen({ tab: section.tab, title: section.title })}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 ${c.bg} text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow hover:opacity-90 transition-all`}
                                                    >
                                                        <Maximize2 size={11} /> Pantalla completa
                                                    </button>
                                                </div>
                                                <DashboardEmbed tab={section.tab} height={700} />
                                            </div>

                                            {/* Step nav footer */}
                                            <div className="flex items-center justify-between px-7 py-5 bg-white border-t border-slate-100">
                                                <button
                                                    onClick={() => setActiveStep(p => ({ ...p, [section.id]: Math.max(0, i - 1) }))}
                                                    disabled={i === 0}
                                                    className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-all"
                                                >
                                                    ← Anterior
                                                </button>
                                                <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{i + 1} / {section.steps.length}</span>
                                                {i < section.steps.length - 1 ? (
                                                    <button
                                                        onClick={() => setActiveStep(p => ({ ...p, [section.id]: i + 1 }))}
                                                        className={`flex items-center gap-1.5 px-6 py-2.5 ${c.bg} text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md hover:opacity-90 transition-all`}
                                                    >
                                                        Siguiente <ArrowRight size={12} />
                                                    </button>
                                                ) : (
                                                    <div className={`flex items-center gap-1.5 px-5 py-2.5 ${c.light} ${c.text} rounded-xl text-[10px] font-black uppercase tracking-widest`}>
                                                        <CheckCircle2 size={12} /> Completado
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* CTA */}
                <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-[2.5rem] p-10 text-center text-white mt-8">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Star size={28} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-black uppercase italic tracking-tighter mb-2">¿Listo para comenzar?</h2>
                    <p className="text-indigo-200 text-sm font-medium mb-6 max-w-sm mx-auto">
                        Accede a tu panel veterinario y empieza a gestionar tu clínica de forma profesional.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button onClick={() => navigate('/veterinarias')} className="flex items-center justify-center gap-2 bg-white text-indigo-700 px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:scale-105 transition-all">
                            <Play size={14} /> Ir al Dashboard
                        </button>
                        <a href="mailto:soporte@amigopeludo.cl" className="flex items-center justify-center gap-2 bg-white/10 text-white border border-white/20 px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-white/20 transition-all">
                            <Mail size={14} /> Contactar Soporte
                        </a>
                    </div>
                </div>
            </main>

            <footer className="border-t border-slate-100 bg-white py-8 mt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
                            <Stethoscope size={14} className="text-white" />
                        </div>
                        <span className="font-black text-slate-700 italic uppercase tracking-tighter text-sm">Amigo Peludo Pro</span>
                    </div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Entorno Veterinario · amigopeludo.cl</p>
                    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-[10px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-700 transition-colors">
                        Volver arriba ↑
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default VetTutorial;
